import { useSceneEvents } from "@/components/common/scene-events-context";
import { isUnknownObject } from "@/object-cache-type-guard";
import { useAppDispatch, useAppStore } from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import {
  changeToolVisibility,
  ToolName,
  ToolVisibility,
} from "@/store/ui/ui-slice";
import { attachPass } from "@faro-lotv/app-component-toolbox";
import { AdaptivePointsMaterial, LodPointCloud } from "@faro-lotv/lotv";
import { useFrame, useThree } from "@react-three/fiber";
import { useEffect, useRef, useState, type JSX } from "react";
import { useStore } from "zustand";
import { fitDepthSurface } from "./fit-depth-surface";
import { expandSurface } from "./expand-surface";
import {
  SurfaceSelectionPass,
  type SurfaceSelection,
} from "./surface-selection-pass";
import { clearSurfaceWand, useSurfaceWandStore } from "./surface-wand-context";
import { SurfacePointFilter } from "./surface-point-filter";
import { CylinderFitWorkerClient } from "./cylinder-fit-worker-client";
import { PipeTrackerWorkerClient } from "./pipe-tracker-worker-client";
import { PipeSelectionGpu } from "./pipe-selection-gpu";
import { PipeSelectionPass } from "./pipe-selection-pass";
import { isAnalyticSelection, isPipeRunSelection } from "./wand-selection";
import { runPipeWandRequest } from "./run-pipe-wand-request";

/** @returns The optional canvas-local selection pass. */
export function SurfaceWandEffects({
  pointCloud,
}: {
  pointCloud: LodPointCloud | null;
}): JSX.Element | null {
  const store = useSurfaceWandStore();
  const camera = useThree((state) => state.camera);
  if (!store || !pointCloud) return null;
  // Remount per camera: the pass and filter are bound to the camera they were built with.
  return (
    <SelectionPass
      key={`${pointCloud.uuid}:${camera.uuid}`}
      store={store}
      pointCloud={pointCloud}
    />
  );
}

function SelectionPass({
  store,
  pointCloud,
}: {
  store: NonNullable<ReturnType<typeof useSurfaceWandStore>>;
  pointCloud: LodPointCloud;
}): JSX.Element {
  const camera = useThree((state) => state.camera);
  const dispatch = useAppDispatch();
  const appStore = useAppStore();
  const sourceVersion = useRef<number[] | undefined>(undefined);
  const nextVersion = useRef<number[]>([]);
  const expansion = useRef<AbortController | undefined>(undefined);
  const [pass] = useState(() => new SurfaceSelectionPass(camera));
  const [pipeGpu] = useState(() => new PipeSelectionGpu());
  const [pipePass] = useState(() => new PipeSelectionPass(camera, pipeGpu));
  const [pointFilter] = useState(
    () => new SurfacePointFilter(camera, pipeGpu, true),
  );
  const [displayAvailable, setDisplayAvailable] = useState(true);
  const [filterInstalled, setFilterInstalled] = useState(false);
  const backgroundOpacity = useStore(
    store,
    (state) => state.otherPointsOpacity,
  );
  const hasSelections = useStore(
    store,
    (state) =>
      state.selection?.cameraId === camera.uuid ||
      state.retainedSelections.some(
        (surface) => surface.cameraId === camera.uuid,
      ),
  );
  const pendingSeed = useStore(
    store,
    (state) => state.request?.cameraId === camera.uuid,
  );
  const retained = useStore(store, (state) => state.retainedSelections);
  const isolated = useStore(
    store,
    (state) =>
      state.isolateSelection && state.selection?.cameraId === camera.uuid,
  );
  const cloudId = isUnknownObject(pointCloud)
    ? pointCloud.iElement.id
    : undefined;
  const retainedForCloud = retained.filter(
    (surface) =>
      surface.cloudId === cloudId && surface.cameraId === camera.uuid,
  );
  const selection = useStore(store, (state) =>
    state.selection &&
    state.selection.cloudId === cloudId &&
    state.selection.cameraId === camera.uuid
      ? state.selection
      : undefined,
  );
  const request = useStore(store, (state) =>
    state.request &&
    state.request.cloudId === cloudId &&
    state.request.cameraId === camera.uuid
      ? state.request
      : undefined,
  );
  const events = useSceneEvents();
  const invalidate = useThree((state) => state.invalidate);
  useEffect(() => {
    if (!request) return;
    dispatch(
      changeToolVisibility({
        name: ToolName.surfaceWand,
        visibility: ToolVisibility.waiting,
      }),
    );
    return () => {
      if (selectActiveTool(appStore.getState()) === ToolName.surfaceWand) {
        dispatch(
          changeToolVisibility({
            name: ToolName.surfaceWand,
            visibility: ToolVisibility.visible,
          }),
        );
      }
    };
  }, [request, dispatch, appStore]);
  useEffect(
    () => () => {
      expansion.current?.abort();
      pass.dispose();
      pipePass.dispose();
      pipeGpu.dispose();
      pointFilter.dispose();
      const {
        request: pending,
        selection: selected,
        retainedSelections,
      } = store.getState();
      if (
        (pending?.cloudId === cloudId && pending?.cameraId === camera.uuid) ||
        (selected?.cloudId === cloudId && selected?.cameraId === camera.uuid) ||
        retainedSelections.some(
          (surface) =>
            surface.cloudId === cloudId && surface.cameraId === camera.uuid,
        )
      ) {
        clearSurfaceWand(store);
      }
    },
    [pass, pipePass, pipeGpu, pointFilter, store, cloudId, camera.uuid],
  );
  useEffect(() => {
    pass.setSelection(
      selection && isAnalyticSelection(selection) ? selection : undefined,
    );
    events.invalidatePointCloudScene.emit();
    invalidate();
  }, [selection, pass, events, invalidate]);
  const filterEnabled =
    hasSelections &&
    displayAvailable &&
    (isolated || backgroundOpacity < 1) &&
    !pendingSeed;
  useEffect(() => {
    if (filterEnabled) setFilterInstalled(true);
  }, [filterEnabled]);
  useEffect(() => {
    const displayed = [
      ...retained.filter(
        (surface) =>
          !isolated &&
          surface.cloudId === cloudId &&
          surface.cameraId === camera.uuid,
      ),
      ...(selection ? [selection] : []),
    ];
    try {
      pipeGpu.setMasks(
        displayed.filter(isPipeRunSelection).map((surface) => surface.mask),
      );
      pointFilter.setSelections(displayed.filter(isAnalyticSelection));
      setDisplayAvailable(true);
    } catch (error: unknown) {
      expansion.current?.abort();
      setDisplayAvailable(false);
      store.setState((state) => ({
        error: `${error instanceof Error ? error.message : "Pipe selection display could not be updated"}. Point filtering has been disabled; the last selection was kept.`,
        ...(state.selection?.coverage === "growing"
          ? { selection: { ...state.selection, coverage: "partial" } }
          : {}),
      }));
    }
    events.invalidatePointCloudScene.emit();
    invalidate();
  }, [
    filterEnabled,
    isolated,
    pointFilter,
    pipeGpu,
    store,
    retained,
    selection,
    cloudId,
    camera.uuid,
    events,
    invalidate,
  ]);
  useEffect(() => {
    pointFilter.setOpacity(isolated ? 0 : backgroundOpacity);
    pointFilter.setEnabled(filterEnabled);
    events.invalidatePointCloudScene.emit();
    invalidate();
  }, [
    pointFilter,
    backgroundOpacity,
    isolated,
    filterEnabled,
    events,
    invalidate,
  ]);
  useEffect(() => {
    if (!filterInstalled) return;
    const restore = pointFilter.attach(pointCloud.material);
    events.invalidatePointCloudScene.emit();
    invalidate();
    return () => {
      restore();
      events.invalidatePointCloudScene.emit();
      invalidate();
    };
  }, [filterInstalled, pointFilter, pointCloud, events, invalidate]);
  useEffect(() => {
    const unsubscribe = store.subscribe((state) => {
      if (state.generation !== generation) expansion.current?.abort();
    });
    const { generation } = store.getState();
    return unsubscribe;
  }, [store, request]);
  useEffect(() => {
    if (!request) {
      pass.capture = undefined;
      return;
    }
    expansion.current?.abort();
    const controller = new AbortController();
    expansion.current = controller;
    const { generation } = store.getState();
    function cancelled(): boolean {
      return (
        controller.signal.aborted || store.getState().generation !== generation
      );
    }
    function retainedSelection(): { selection?: typeof selection } {
      const previous = request?.addTo;
      if (!previous) return {};
      return {
        selection: {
          ...previous,
          coverage:
            previous.coverage === "growing" ? "partial" : previous.coverage,
        },
      };
    }
    pass.captureShape =
      request.shape === "pipe-run" ? "cylinder" : (request.shape ?? "plane");
    pass.capturePoint = request.point;
    pass.capture = (snapshot) => {
      let fitting: CylinderFitWorkerClient | undefined;
      let tracing: PipeTrackerWorkerClient | undefined;
      void Promise.resolve()
        .then(async (): Promise<SurfaceSelection | undefined> => {
          if (cancelled()) return;
          if (request.shape === "cylinder" || request.shape === "pipe-run") {
            fitting = new CylinderFitWorkerClient(
              new Worker(new URL("./cylinder-fit-worker.ts", import.meta.url), {
                type: "module",
              }),
              controller.signal,
            );
          }
          if (request.shape === "pipe-run" && fitting) {
            tracing = new PipeTrackerWorkerClient(
              new Worker(new URL("./pipe-tracker-worker.ts", import.meta.url), {
                type: "module",
              }),
              controller.signal,
            );
            await runPipeWandRequest(pointCloud, snapshot, request, store, {
              signal: controller.signal,
              cancelled,
              fitCylinder: fitting.fit,
              solver: tracing,
            });
            return;
          }
          return fitDepthSurface(snapshot, request, cancelled, fitting?.fit);
        })
        .then(async (result) => {
          if (cancelled() || request.shape === "pipe-run") return;
          let failureMessage =
            request.shape === "cylinder"
              ? "No stable cylinder found. Try a view with clearly resolved curvature on a straight section, away from ends or intersections."
              : "No connected flat surface found. Click closer to the center of a flat surface.";
          if (request.addTo) {
            failureMessage =
              "Click on the current surface to add a seed. The existing selection was kept.";
          }
          store.setState({
            request: undefined,
            ...(result
              ? { selection: { ...result, coverage: "growing" } }
              : retainedSelection()),
            error: result ? undefined : failureMessage,
          });
          if (result) {
            await expandSurface(
              pointCloud,
              result,
              controller.signal,
              (expanded) => {
                if (!cancelled()) store.setState({ selection: expanded });
              },
              fitting?.fit,
            ).catch(() => {
              if (!cancelled()) {
                const selected = store.getState().selection;
                if (selected) {
                  store.setState({
                    selection: { ...selected, coverage: "partial" },
                  });
                }
              }
            });
          }
        })
        .catch((error: unknown) => {
          if (!cancelled()) {
            store.setState((state) => ({
              request: undefined,
              ...retainedSelection(),
              ...(state.selection?.coverage === "growing"
                ? { selection: { ...state.selection, coverage: "partial" } }
                : {}),
              error:
                request.shape === "pipe-run" && error instanceof Error
                  ? error.message
                  : "Surface selection is unavailable for this view.",
            }));
          }
        })
        .finally(() => {
          fitting?.dispose();
          tracing?.dispose();
        });
    };
    events.invalidatePointCloudScene.emit();
    invalidate();
    return () => {
      pass.capture = undefined;
    };
  }, [request, pass, store, events, invalidate, pointCloud]);
  useFrame(() => {
    pass.camera = camera;
    const cadComparison =
      pointCloud.material instanceof AdaptivePointsMaterial &&
      pointCloud.material.defines.USE_DISTANCE_COLORMAP === 1;
    pass.enabled = !!request || (!!selection && !cadComparison);
    pipePass.enabled =
      displayAvailable &&
      !cadComparison &&
      !pendingSeed &&
      ((!!selection && isPipeRunSelection(selection)) ||
        (!isolated && retainedForCloud.some(isPipeRunSelection)));
    if (!pass.enabled && !retainedForCloud.length) {
      sourceVersion.current = undefined;
      return;
    }
    const version = writeSourceVersion(nextVersion.current, pointCloud);
    const previous = sourceVersion.current;
    if (
      previous &&
      (previous.length !== version.length ||
        version.some((value, index) => value !== previous[index]))
    ) {
      clearSurfaceWand(store);
      pass.enabled = false;
      pipePass.enabled = false;
    }
    // Swap buffers so the next frame reuses the old array.
    nextVersion.current = previous ?? [];
    sourceVersion.current = version;
  });
  return (
    <>
      <primitive object={pass} attach={attachPass} />
      <primitive object={pipePass} attach={attachPass} />
      {!isolated &&
        retainedForCloud
          .filter(isAnalyticSelection)
          .map((surface) => (
            <RetainedSelectionPass
              key={surface.id}
              selection={surface}
              pointCloud={pointCloud}
            />
          ))}
      <primitive object={pointFilter} attach={attachPass} />
    </>
  );
}

/**
 * @param out Reused buffer, overwritten in place.
 * @param pointCloud Source whose pose, visibility, clipping or scan filter invalidates selections.
 * @returns `out`, holding a flat numeric snapshot of those inputs.
 */
function writeSourceVersion(
  out: number[],
  pointCloud: LodPointCloud,
): number[] {
  const { material } = pointCloud;
  const planes = material.clippingPlanes ?? [];
  const hidden =
    material instanceof AdaptivePointsMaterial ? material.hiddenScans : [];
  out.length = 0;
  out.push(...pointCloud.matrixWorld.elements, pointCloud.visible ? 1 : 0);
  out.push(planes.length);
  for (const { normal, constant } of planes) {
    out.push(normal.x, normal.y, normal.z, constant);
  }
  out.push(...hidden);
  return out;
}

function RetainedSelectionPass({
  selection,
  pointCloud,
}: {
  selection: SurfaceSelection;
  pointCloud: LodPointCloud;
}): JSX.Element {
  const camera = useThree((state) => state.camera);
  const invalidate = useThree((state) => state.invalidate);
  const events = useSceneEvents();
  const [pass] = useState(() => new SurfaceSelectionPass(camera));
  useEffect(() => {
    pass.camera = camera;
    pass.setSelection(selection);
    pass.enabled = true;
    events.invalidatePointCloudScene.emit();
    invalidate();
    return () => {
      pass.setSelection(undefined);
      pass.enabled = false;
      events.invalidatePointCloudScene.emit();
      invalidate();
    };
  }, [pass, selection, camera, events, invalidate]);
  useEffect(() => () => pass.dispose(), [pass]);
  useFrame(() => {
    pass.enabled = !(
      pointCloud.material instanceof AdaptivePointsMaterial &&
      pointCloud.material.defines.USE_DISTANCE_COLORMAP === 1
    );
  });
  return <primitive object={pass} attach={attachPass} />;
}
